// Marketplace-specific config for the Product Image Resizer wrapper pages.
export const IMAGE_VARIANTS = {
  amazon: {
    path: "/tools/amazon-product-image-resizer",
    title: "Amazon Product Image Resizer",
    promise: "Resize product photos to Amazon's 1000×1000px main-image requirement, right in your browser.",
    defaultPreset: "square-1000",
    seoTitle: "Amazon Product Image Resizer - Free, No Upload",
    seoDesc: "Resize product images to Amazon's listing requirements (1000x1000px main image, square crop) in your browser. Free, no login, no upload.",
    jsonName: "Amazon Product Image Resizer",
    content: {
      intro: [
        "Amazon requires main listing images to be at least 1000×1000 pixels on the longest side (to enable zoom) and generally expects a pure white background for the main image, with the product filling most of the frame. Secondary images have more flexibility on background but still need to meet minimum resolution.",
        "This page uses the site's general Product Image Resizer with presets matching Amazon's common size requirements, so you don't have to calculate dimensions manually.",
      ],
      howTo: [
        "Upload your product photo (it stays in your browser, not on a server).",
        "Choose the \"Square 1000 × 1000\" preset for the main image, or enter a custom size for secondary images.",
        "Crop or reposition if the resize changes your framing.",
        "Preview the result against Amazon's white-background requirement for main images.",
        "Download and upload directly to your Amazon listing.",
      ],
      requirements: {
        title: "Amazon image requirements at a glance",
        items: [
          "Main image: minimum 1000px on the longest side (zoom requires this); pure white (RGB 255,255,255) background; product should fill 85%+ of the frame; no text, watermarks, or additional objects.",
          "Secondary images: same minimum resolution; background isn't restricted, so lifestyle shots, size charts, and close-ups are allowed.",
          "Always confirm current requirements in Seller Central, since category-specific rules (like apparel model shots) can differ.",
        ],
      },
      faq: [
        { q: "What happens if my main image is under 1000×1000px?", a: "Amazon may reject the listing or disable zoom on the image. Upscale your source photo or reshoot at a higher resolution before resizing." },
        { q: "Does this tool remove the background for me?", a: "No, this tool resizes and crops to the correct dimensions; background removal isn't included. You'll need a separate background-removal step if your source photo isn't already on white." },
        { q: "Can I resize multiple product photos in one session?", a: "Yes, upload and resize photos one at a time, or batch through them sequentially — there's no combined multi-file export yet." },
        { q: "Is this different from Meesho's image requirements?", a: "Yes — Meesho doesn't require a pure white background for all categories, and its recommended dimensions differ. Use the Meesho Product Image Resizer for that marketplace." },
      ],
      links: [
        { label: "Meesho Product Image Resizer", path: "/tools/meesho-product-image-resizer" },
        { label: "Product Image Resizer", path: "/tools/product-image-resizer" },
        { label: "Amazon Product Title Generator", path: "/ai-tools/amazon-product-title-generator" },
        { label: "Guide: How to Use the AI Listing Tools", path: "/blog/how-to-use-ai-listing-tools" },
      ],
    },
  },
  meesho: {
    path: "/tools/meesho-product-image-resizer",
    title: "Meesho Product Image Resizer",
    promise: "Resize product photos to a Meesho-friendly square crop, right in your browser.",
    defaultPreset: "square-1080",
    seoTitle: "Meesho Product Image Resizer – Free, No Upload",
    seoDesc: "Resize product images to Meesho's listing requirements (square crop, min resolution) in your browser. Free, no login, no server upload.",
    jsonName: "Meesho Product Image Resizer",
    content: {
      intro: [
        "Meesho recommends square product images with the product clearly visible and well-lit, and unlike Amazon it doesn't mandate a pure white background for every category — lifestyle and in-use shots are common on Meesho listings.",
        "This page uses the site's general Product Image Resizer with a Meesho-friendly square preset, so you can get your photos to the right dimensions quickly.",
      ],
      howTo: [
        "Upload your product photo (processed in your browser, not uploaded to a server).",
        "Choose the square crop preset, or enter a custom size.",
        "Reposition the crop so the product stays centered and fully visible.",
        "Preview the result.",
        "Download and upload to your listing in the Meesho Seller Panel.",
      ],
      requirements: {
        title: "Meesho image guidelines at a glance",
        items: [
          "Square aspect ratio is recommended for consistent display across the app.",
          "Background flexibility is higher than Amazon's — plain, lifestyle, or in-use shots are all commonly accepted.",
          "Product should be clearly lit and in focus, filling most of the frame.",
          "Always check the current Seller Panel guidelines, since some categories (like fashion) may have additional recommendations, such as model shots.",
        ],
      },
      faq: [
        { q: "Does Meesho require a white background like Amazon?", a: "No, Meesho is more flexible on background — plain and lifestyle images are both widely used, though a clean, uncluttered background still performs better than a busy one." },
        { q: "What's the minimum resolution Meesho accepts?", a: "Meesho doesn't publish as strict a pixel minimum as Amazon, but low-resolution or blurry images hurt conversion regardless — aim for at least 800×800px." },
        { q: "Can I use the same resized image for Amazon too?", a: "You can, but check it against Amazon's stricter white-background and 1000×1000px requirements first — use the Amazon Product Image Resizer for that marketplace specifically." },
        { q: "Is my image uploaded to a server during resizing?", a: "No, resizing happens entirely in your browser." },
      ],
      links: [
        { label: "Amazon Product Image Resizer", path: "/tools/amazon-product-image-resizer" },
        { label: "Product Image Resizer", path: "/tools/product-image-resizer" },
        { label: "Meesho Product Title Generator", path: "/ai-tools/meesho-product-title-generator" },
        { label: "Guide: How to Use the AI Listing Tools", path: "/blog/how-to-use-ai-listing-tools" },
      ],
    },
  },
};
